import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Trash2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import api from "@/lib/api-client";
import { useConfirm } from "@/contexts/ConfirmContext";
import { useGlobalToast } from "@/contexts/ToastContext";
import {
  getNotificationLink,
  getChangeRequestReservationLink,
  getNotificationTypeVisual,
  notificationTypeLabel,
} from "@/lib/notifications";

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

interface NotificationsPagination {
  total: number;
  limit: number;
  offset: number;
  totalPages: number;
}

interface NotificationTypeCount {
  type: string;
  count: number;
}

const PAGE_SIZE = 20;

const DEFAULT_PAGINATION: NotificationsPagination = {
  total: 0,
  limit: PAGE_SIZE,
  offset: 0,
  totalPages: 0,
};

export default function NotificationsHistory() {
  const { confirm } = useConfirm();
  const { showToast } = useGlobalToast();

  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [pagination, setPagination] =
    useState<NotificationsPagination>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [types, setTypes] = useState<NotificationTypeCount[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const fetchNotifications = useCallback(
    async (offset = 0) => {
      setLoading(true);
      try {
        const params: Record<string, string> = {
          limit: String(PAGE_SIZE),
          offset: String(offset),
        };
        if (unreadOnly) params.unreadOnly = "true";
        if (typeFilter !== "all") params.type = typeFilter;

        const response = await api.get("/notifications", { params });
        if (response.data.success) {
          setNotifications(response.data.data);
          setPagination(response.data.pagination ?? DEFAULT_PAGINATION);
        }
      } catch {
        showToast("No se pudieron cargar las notificaciones", "error");
      } finally {
        setLoading(false);
      }
    },
    [unreadOnly, typeFilter, showToast],
  );

  // Se dispara al montar y cada vez que cambia un filtro (unreadOnly/typeFilter
  // están en las dependencias de fetchNotifications), reseteando siempre a la
  // primera página. Igual que Emails.tsx/WhatsApp.tsx en /admin.
  useEffect(() => {
    fetchNotifications(0);
  }, [fetchNotifications]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await api.get("/notifications/types");
        if (response.data.success) setTypes(response.data.data);
      } catch {
        // Silently ignore: el select de tipo simplemente queda solo con "Todos".
      }
    };
    fetchTypes();
  }, []);

  const markAsRead = async (id: string) => {
    try {
      await api.post("/notifications/mark-read", { notificationIds: [id] });
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n,
        ),
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleRowClick = (notification: NotificationItem) => {
    if (!notification.isRead) {
      void markAsRead(notification.id);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          isRead: true,
          readAt: new Date().toISOString(),
        })),
      );
      showToast("Todas las notificaciones se marcaron como leídas", "success");
    } catch {
      showToast("No se pudieron marcar todas como leídas", "error");
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleDelete = async (
    notification: NotificationItem,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const confirmed = await confirm({
      title: "¿Eliminar esta notificación?",
      message: "Esta acción no se puede deshacer.",
      confirmText: "Sí, eliminar",
      danger: true,
    });
    if (!confirmed) return;

    setDeletingId(notification.id);
    try {
      await api.delete(`/notifications/${notification.id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      setPagination((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
      }));
      showToast("Notificación eliminada", "success");
    } catch {
      showToast("No se pudo eliminar la notificación", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const hasUnread = notifications.some((n) => !n.isRead);
  const currentPage =
    Math.floor(pagination.offset / (pagination.limit || PAGE_SIZE)) + 1;

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Notificaciones
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Histórico completo de avisos de tu organización
          </p>
        </div>
        <button
          type="button"
          onClick={handleMarkAllRead}
          disabled={markingAllRead || !hasUnread}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {markingAllRead ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CheckCheck className="w-4 h-4" />
          )}
          Marcar todas como leídas
        </button>
      </div>

      {/* Filtros */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden w-fit">
          <button
            type="button"
            onClick={() => setUnreadOnly(false)}
            className={`px-3 py-2 text-sm font-medium transition-colors ${
              !unreadOnly
                ? "bg-primary-600 text-white"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Todas
          </button>
          <button
            type="button"
            onClick={() => setUnreadOnly(true)}
            className={`px-3 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${
              unreadOnly
                ? "bg-primary-600 text-white"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            No leídas
          </button>
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-fit"
        >
          <option value="all">Todos los tipos</option>
          {types.map((t) => (
            <option key={t.type} value={t.type}>
              {notificationTypeLabel(t.type)} ({t.count})
            </option>
          ))}
        </select>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-16 text-center text-gray-500">
            <Bell className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-gray-700">
              No hay notificaciones
            </p>
            <p className="text-sm text-gray-400 mt-1">
              {unreadOnly || typeFilter !== "all"
                ? "No hay notificaciones que coincidan con los filtros seleccionados."
                : "Aún no has recibido ninguna notificación."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => {
              // Las solicitudes de cambio no navegan desde la campanita (abren
              // su modal), pero en el historial sí deben llevar a la reserva.
              const link =
                getNotificationLink(notification) ??
                (notification.type === "reservation_change_request"
                  ? getChangeRequestReservationLink(notification.data)
                  : null);
              const { icon: TypeIcon, colorClass } = getNotificationTypeVisual(
                notification.type,
                notification,
              );

              const rowContent = (
                <div
                  className={`flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                    !notification.isRead ? "bg-primary-50/50" : ""
                  }`}
                  onClick={() => handleRowClick(notification)}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                      <TypeIcon className={`w-4 h-4 ${colorClass}`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm ${
                          !notification.isRead
                            ? "font-semibold text-gray-900"
                            : "font-medium text-gray-700"
                        }`}
                      >
                        {notification.title}
                      </p>
                      {!notification.isRead && (
                        <span className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-1.5" />
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDistanceToNow(new Date(notification.createdAt), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </p>
                  </div>

                  {/* Delete */}
                  <button
                    type="button"
                    onClick={(e) => handleDelete(notification, e)}
                    disabled={deletingId === notification.id}
                    title="Eliminar notificación"
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition-colors"
                  >
                    {deletingId === notification.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              );

              return link ? (
                <Link key={notification.id} to={link}>
                  {rowContent}
                </Link>
              ) : (
                <div key={notification.id}>{rowContent}</div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              {pagination.total} notificaciones · Página {currentPage} de{" "}
              {pagination.totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  fetchNotifications(
                    Math.max(0, pagination.offset - pagination.limit),
                  )
                }
                disabled={pagination.offset <= 0 || loading}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() =>
                  fetchNotifications(pagination.offset + pagination.limit)
                }
                disabled={currentPage >= pagination.totalPages || loading}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
